import Table from "react-bootstrap/Table";
import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { SerializedError } from "@reduxjs/toolkit";

import NoGridData from "./NoGridData";
import Badge from "../Badge/Badge";
import { GRID_COL_TYPES, PAGE_SIZES } from "../../constants/globalConstants";
import PageSizeSelect from "./PageSizeselect";
import { useSorting } from "../../utils/handleSort";
import SortingIcon from "./SortingIcon";
import AsyncFormSelect from "../FormSelect/AsyncFormSelect";
import useWindowSize from "../../hooks/useWindowSize";
import "./grid.scss";

interface TableHeaders {
  headerName: string;
  iconComponent?: null | React.ReactNode;
  shouldSort?: boolean;
}

interface GridDataProps<A extends Record<string, any>> {
  tableHeaders: Array<TableHeaders>;
  tableData: Array<A>;
  isLoading: boolean;
  isError: boolean;
  error?: FetchBaseQueryError | SerializedError | any;
  isDataPresent: boolean;
  wordWrapColumn?: Array<string>;
  noDataUIMainText: string;
  noDataUISubText?: string;
  noDataUIButtonLabel?: string;
  handleClick?: () => void;
  showPageSizeSelector?: boolean;
  page_size?: string | number;
  handlePageSizeChange?: (val: string | null) => void;
  sort?: { sortField: string; sortOrder: "asc" | "desc" | undefined };
  asyncFormSelectProps?: any;
  // fetchQuery?: any;
  // fetchQueryParams?: { startKey: string | undefined; page_size: number };
  // tabledatafields?: string[];
  // actionItems?: Array<{ label: string; navigateUrl: string }>;
}

const Grid = <A extends Record<string, any>>({
  tableHeaders,
  tableData,
  isLoading,
  isError,
  error,
  isDataPresent,
  wordWrapColumn,
  noDataUIMainText,
  noDataUISubText,
  noDataUIButtonLabel,
  handleClick,
  showPageSizeSelector,
  page_size = String(PAGE_SIZES.Default_List_Size),
  handlePageSizeChange,
  sort,
  asyncFormSelectProps,
  // fetchQuery,
  // fetchQueryParams,
  // tabledatafields,
  // actionItems,
}: GridDataProps<A>) => {
  const tableHeadersLength = tableHeaders.length;
  const { Action_Icon, Check_Element } = GRID_COL_TYPES;
  // const uniqueToastId = `error_${Date.now()}`;
  const { sortOrder, handleSort } = useSorting({
    sortField: sort?.sortField,
    order: sort?.sortOrder,
  });

  const { width } = useWindowSize();

  const { field, direction } = sortOrder;

  const sortedTableData = tableHeaders?.some(
    (header) => header.headerName === sort?.sortField
  )
    ? tableData?.sort((a: any, b: any) => {
        const aValue =
          typeof a[field] === "string" ? a[field] : a[field]?.[0]?.text;
        const bValue =
          typeof b[field] === "string" ? b[field] : b[field]?.[0]?.text;
        if (!aValue || !bValue) return null;
        if (direction === "asc") {
          return aValue.localeCompare(bValue);
        } else {
          return bValue.localeCompare(aValue);
        }
      })
    : tableData;

  // const handleFetchData = async () => {
  //   try {
  //     const response = await fetchQuery(fetchQueryParams).unwrap();
  //     console.log(response);
  //   } catch (error) {
  //     console.log(error);
  //   }
  // };

  // useEffect(() => {
  //   handleFetchData();
  // }, []);

  return (
    <>
      {/* note: can be used, if needed in future */}
      {/* <div className="mt-5">
          <h6 className="m-0 fs-14">
            SHOWING <span className="highlight-text">{tableData?.length}</span> of{" "}
            <span className="highlight-text">{totalDataLength}</span> ACCOUNTS
          </h6>
        </div> */}

      {/* grid code */}
      <div className="d-sm-flex justify-content-between mb-2" id="grid-filter">
        <div className="row w-100 px-0 mx-0">
          {asyncFormSelectProps &&
            asyncFormSelectProps?.map((eachProp: any, index: number) => (
              <div key={index} className="col-sm-12 col-md-6 px-0 pe-sm-3">
                <AsyncFormSelect {...eachProp} />
              </div>
            ))}{" "}
        </div>
        <div
          className={`${asyncFormSelectProps ? (width > 580 ? "w-50" : "w-100 ") : "w-100"} d-flex align-items-end justify-content-end mt-3 mt-sm-0`}
        >
          {showPageSizeSelector && (
            <PageSizeSelect
              value={page_size as string}
              onChange={handlePageSizeChange}
              disable={!isDataPresent}
              className="z-1"
            />
          )}
        </div>
      </div>

      <div className={!isLoading && !isDataPresent ? "" : "table-max-height"}>
        <Table hover responsive className="m-0 fs-14">
          <thead className="table-header">
            <tr>
              {tableHeaders.map((data, i) => (
                <th key={data.headerName} tabIndex={i}>
                  {data.headerName}{" "}
                  {data.shouldSort && (
                    <>
                      <SortingIcon
                        onClick={() => handleSort(data.headerName)}
                        direction={
                          sortOrder.field === data.headerName
                            ? sortOrder.direction
                            : ""
                        }
                      />
                    </>
                  )}
                  {data.iconComponent && data.iconComponent}
                </th>
              ))}
            </tr>
          </thead>

          <tbody className="table-body">
            {!isLoading &&
              (!isDataPresent ? (
                <NoGridData
                  buttonLabel={noDataUIButtonLabel}
                  tableHeadersLength={tableHeadersLength}
                  mainText={noDataUIMainText}
                  subText={noDataUISubText}
                  handleClick={handleClick}
                />
              ) : (
                sortedTableData.map((tData, i) => {
                  const keyArr = Object.keys(tData);

                  return (
                    <tr key={i}>
                      {keyArr.map((tDataKey, keyIndex) => {
                        const isActionIcon = tDataKey === Action_Icon;
                        const isCheckElement = tDataKey === Check_Element;
                        const isWordWrapColumn =
                          wordWrapColumn?.includes(tDataKey);
                        return (
                          <td
                            key={keyIndex}
                            tabIndex={keyIndex}
                            className={`${isActionIcon ? "icon td-visible" : ""} ${isCheckElement ? "check-element-col" : ""} ${isWordWrapColumn && "word-wrap"}`}
                          >
                            <div
                              className={
                                isActionIcon ? "position-relative" : ""
                              }
                            >
                              {Array.isArray(tData[tDataKey]) ? (
                                <Badge items={tData[tDataKey]} />
                              ) : (
                                tData[tDataKey]
                              )}
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  );
                })
              ))}
          </tbody>
        </Table>
      </div>
    </>
  );
};

export default Grid;
